import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Textarea } from '@/app/components/ui/textarea';
import { Input } from '@/app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useCorrectiveAction, useUpdateCorrectiveAction } from '@/hooks/use-corrective-actions';
import { useEffect, useState } from 'react';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { CorrectiveActionStatus } from '@/lib/types';

const STATUS_LABELS: Record<CorrectiveActionStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  pending_verification: 'Pending Verification',
  closed: 'Closed',
  overdue: 'Overdue',
};

const STATUS_VARIANTS: Record<CorrectiveActionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  open: 'default',
  in_progress: 'secondary',
  pending_verification: 'outline',
  closed: 'secondary',
  overdue: 'destructive',
};

const SOURCE_ROUTES: Record<string, string> = {
  complianceincidentreport: '/compliance/incidents',
  managementreview: '/compliance/reviews',
};

const SOURCE_LABELS: Record<string, string> = {
  incident: 'Incident',
  audit_finding: 'Audit Finding',
  management_review: 'Management Review',
  alert: 'Alert',
  manual: 'Manual',
  nc_assessment: 'NC Assessment',
};

export function ActionDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const actionId = Number(id);
  const { data: action, isLoading } = useCorrectiveAction(actionId);
  const updateAction = useUpdateCorrectiveAction();

  const [actionPlan, setActionPlan] = useState('');
  const [effectivenessNotes, setEffectivenessNotes] = useState('');
  const [effectivenessDate, setEffectivenessDate] = useState('');

  useEffect(() => {
    setActionPlan(action?.action_plan ?? '');
  }, [actionId, action?.action_plan]);

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Loading...</div>;
  }

  if (!action) {
    return <div className="py-8 text-center text-muted-foreground">Action not found</div>;
  }

  const sourceRoute = action.source_content_type_model
    ? SOURCE_ROUTES[action.source_content_type_model]
    : null;
  const sourceLink =
    sourceRoute && action.source_object_id
      ? `${sourceRoute}/${action.source_object_id}`
      : null;

  function handleStatusChange(newStatus: CorrectiveActionStatus) {
    updateAction.mutate({ id: actionId, status: newStatus });
  }

  function handleActionPlanSubmit() {
    updateAction.mutate({ id: actionId, action_plan: actionPlan });
  }

  function handleEffectivenessSubmit() {
    updateAction.mutate({
      id: actionId,
      effectiveness_notes: effectivenessNotes,
      effectiveness_review_date: effectivenessDate || null,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/compliance/actions">
          <Button variant="ghost" size="sm">
            &larr; Back to CAPA Register
          </Button>
        </Link>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{action.title}</h1>
          <div className="flex gap-2 mt-2">
            <Badge variant={STATUS_VARIANTS[action.status as CorrectiveActionStatus]}>
              {STATUS_LABELS[action.status as CorrectiveActionStatus]}
            </Badge>
            <Badge variant="outline" className="capitalize">
              {action.action_type}
            </Badge>
            <Badge variant="outline" className="capitalize">
              {action.source_type.replace('_', ' ')}
            </Badge>
          </div>
        </div>
        <Select value={action.status} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Change Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="pending_verification">Pending Verification</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1 whitespace-pre-wrap">{action.description}</p>
            </div>
            {action.root_cause && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Root Cause</label>
                <p className="mt-1 whitespace-pre-wrap">{action.root_cause}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Assigned To</label>
                <p className="mt-1">{action.assigned_to_username ?? '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Due Date</label>
                <p className="mt-1">{formatDateOnly(action.due_date)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created</label>
                <p className="mt-1">{formatDateTimeInTimeZone(action.created_at, timeZone, { dateStyle: 'medium' })}</p>
              </div>
              {action.completed_at && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Completed</label>
                  <p className="mt-1">{formatDateTimeInTimeZone(action.completed_at, timeZone, { dateStyle: 'medium' })}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Source</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Source Type</label>
              <p className="mt-1">{SOURCE_LABELS[action.source_type] ?? action.source_type.replace('_', ' ')}</p>
            </div>
            {action.clause_ref && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Clause Reference</label>
                <p className="mt-1 font-mono text-sm">{action.clause_ref}</p>
                {action.clause_title && (
                  <p className="text-sm text-muted-foreground">{action.clause_title}</p>
                )}
              </div>
            )}
            {sourceLink ? (
              <Link to={sourceLink}>
                <Button variant="outline" size="sm">
                  View Source Record
                </Button>
              </Link>
            ) : (
              !action.clause_ref && (
                <p className="text-sm text-muted-foreground">No linked source record</p>
              )
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Action Plan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={actionPlan}
            onChange={(e) => setActionPlan(e.target.value)}
            placeholder="Describe the steps to address this CAPA..."
            className="mt-1"
            rows={5}
          />
          <Button onClick={handleActionPlanSubmit} disabled={updateAction.isPending}>
            Save Action Plan
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Effectiveness Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {action.effectiveness_verified_by_username && (
            <div className="rounded-md bg-muted p-4">
              <p className="text-sm">
                <span className="font-medium">Verified by:</span>{' '}
                {action.effectiveness_verified_by_username}
              </p>
              {action.effectiveness_review_date && (
                <p className="text-sm">
                  <span className="font-medium">Review date:</span>{' '}
                  {formatDateOnly(action.effectiveness_review_date)}
                </p>
              )}
              {action.effectiveness_notes && (
                <p className="text-sm mt-2 whitespace-pre-wrap">
                  {action.effectiveness_notes}
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Review Date</label>
              <Input
                type="date"
                value={effectivenessDate}
                onChange={(e) => setEffectivenessDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={effectivenessNotes}
              onChange={(e) => setEffectivenessNotes(e.target.value)}
              placeholder="Describe effectiveness review findings..."
              className="mt-1"
              rows={4}
            />
          </div>
          <Button onClick={handleEffectivenessSubmit} disabled={updateAction.isPending}>
            Save Effectiveness Review
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
